import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  TrendingUp,
  MousePointer,
  DollarSign,
  Eye,
  AlertCircle,
  Loader2,
  BarChart3,
  MessageCircle,
  PieChart
} from 'lucide-react';
import { Card, Button, DateRangePicker, Drawer } from '@/components/ui';
import { useNewsletterStore } from '@/stores/newsletterStore';
import usePermissionsStore from '@/stores/permissionsStore';
import useGlobalCustomerStore from '@/stores/globalCustomerStore';
import { NewsletterKPI, NewsletterPost } from '@/types/newsletter';
import PostDetailDrawer from './PostDetailDrawer';
import NewsletterPostsTable from './NewsletterPostsTable';
import PublicationMetricChart from './PublicationMetricChart';

interface NewsletterDashboardProps {
  customerId: string;
}

const NewsletterDashboard: React.FC<NewsletterDashboardProps> = ({ customerId }) => {
  const { permissions } = usePermissionsStore();
  const { selectedCustomer } = useGlobalCustomerStore();
  const {
    currentStats,
    postsStats,
    historyStats,
    isLoading,
    loadCustomerStats,
    loadCustomerPublications,
    loadPublicationPostsStats,
    loadPublicationHistory
  } = useNewsletterStore();

  const [selectedPost, setSelectedPost] = useState<NewsletterPost | null>(null);
  const [customDateRange, setCustomDateRange] = useState<{ start: string; end: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'posts' | 'publication-stats'>('dashboard');
  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);

  useEffect(() => {
    if (customerId) {
      loadCustomerStats(customerId);
      loadCustomerPublications(customerId);
    }
  }, [customerId]);

  // Load posts only when Posts tab is selected
  useEffect(() => {
    if (activeTab === 'posts' && currentStats?.publication_id && !postsStats) {
      loadPublicationPostsStats(currentStats.publication_id);
    }
  }, [activeTab, currentStats?.publication_id, postsStats]);

  // Load history data when Publication Performance tab is selected
  useEffect(() => {
    if (activeTab === 'publication-stats' && currentStats?.publication_id && historyStats.length === 0) {
      loadPublicationHistory(currentStats.publication_id);
    }
  }, [activeTab, currentStats?.publication_id, historyStats.length]);

  // Emergency fallback - should never be reached
  if (!customerId) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Customer Selected</h3>
          <p className="text-sm text-gray-500">Please select a customer to view newsletter analytics</p>
        </div>
      </div>
    );
  }

  // Calculate derived metrics with safe access - use zeros if no data
  const deliveryRate = (currentStats?.total_emails_sent || 0) > 0
    ? ((currentStats?.total_delivered || 0) / (currentStats?.total_emails_sent || 1)) * 100
    : 0;

  // const engagementScore = (currentStats?.average_open_rate + currentStats?.average_click_rate) / 2;

  // Define KPIs with safe access
  const kpis: NewsletterKPI[] = [
    {
      label: 'Earned Media Value',
      value: currentStats?.email_marketing_value || 0,
      format: 'currency',
      icon: 'dollar-sign',
      change: 12.5,
      changeType: 'increase'
    },
    {
      label: 'Total Sent Emails',
      value: currentStats?.total_emails_sent || 0,
      format: 'number',
      icon: 'users',
      change: 5.2,
      changeType: 'increase'
    },
    {
      label: 'Open Rate',
      value: currentStats?.average_open_rate || 0,
      format: 'percentage',
      icon: 'eye',
      change: 2.1,
      changeType: 'increase'
    },
    {
      label: 'Click Rate',
      value: currentStats?.average_click_rate || 0,
      format: 'percentage',
      icon: 'mouse-pointer',
      change: -0.5,
      changeType: 'decrease'
    }
  ];

  const openRate = currentStats?.average_open_rate || 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{selectedCustomer?.customerName || 'Newsletter Analytics'}</h1>
          <p className="text-gray-600">{currentStats?.publication_name || 'Publication'}</p>
        </div>

        <div className="flex items-center space-x-2">
          <DateRangePicker
            value={customDateRange}
            onChange={(range, selectedPeriod) => {
              if (selectedPeriod === 'custom' && range) {
                setCustomDateRange(range);
                // Reload data with custom date range
                loadCustomerStats(customerId, 'custom', range.start, range.end);
                // Only reload posts if we're on the posts tab
                if (activeTab === 'posts' && currentStats?.publication_id) {
                  loadPublicationPostsStats(currentStats.publication_id, 'custom', range.start, range.end);
                }
              } else {
                setCustomDateRange(null);
                // Map DateRangePicker presets to API period values
                // API accepts: 'day', 'week', 'month', '30days', '90days'
                let apiPeriod: string = selectedPeriod;
                if (selectedPeriod === '60days') {
                  apiPeriod = '90days'; // Backend only supports 90days, not 60days
                }
                // Reload data with preset period
                loadCustomerStats(customerId, apiPeriod);
                // Only reload posts if we're on the posts tab
                if (activeTab === 'posts' && currentStats?.publication_id) {
                  loadPublicationPostsStats(currentStats.publication_id, apiPeriod);
                }
              }
            }}
            maxRetentionDays={permissions?.effectiveLimits?.post_retention_days || 90}
            disabled={!customerId}
          />
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="px-2 pt-2 pb-0">
        <div className="flex space-x-1 bg-gradient-to-r from-gray-100 to-gray-200 p-1 rounded-lg shadow-inner">
          {[
            {
              id: 'dashboard',
              label: 'Dashboard',
              icon: BarChart3
            },
            {
              id: 'posts',
              label: 'Posts',
              icon: MessageCircle
            },
            {
              id: 'publication-stats',
              label: 'Publication Performance',
              icon: PieChart
            }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center px-4 py-2 rounded-md text-xs font-semibold transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-white text-blue-600 shadow-md border border-white/50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              <tab.icon className="h-3.5 w-3.5 mr-1.5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content Area with Loading State */}
      {isLoading && !currentStats ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 text-blue-600 animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Newsletter Data</h3>
            <p className="text-sm text-gray-500">Please wait while we fetch your analytics...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Dashboard Tab Content */}
          {activeTab === 'dashboard' && (
            <>
          {/* Hero EMV Card */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 p-6 shadow-lg">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="newsletter-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#newsletter-pattern)" />
          </svg>
        </div>

        <div className="relative flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <DollarSign className="h-5 w-5 text-white" />
              <span className="text-sm font-medium text-white/90">Total Email Marketing Value</span>
            </div>
            <div className="flex items-baseline space-x-3">
              <span className="text-4xl font-bold text-white">
                ${(currentStats?.email_marketing_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              {kpis.find(k => k.label === 'Earned Media Value')?.change && (
                <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                  (kpis.find(k => k.label === 'Earned Media Value')?.changeType === 'increase')
                    ? 'bg-white/20 text-white'
                    : 'bg-red-500/20 text-white'
                }`}>
                  {kpis.find(k => k.label === 'Earned Media Value')?.changeType === 'increase' ? '+' : ''}
                  {kpis.find(k => k.label === 'Earned Media Value')?.change}%
                </span>
              )}
            </div>
            <p className="text-xs text-white/70 mt-1">vs previous period</p>
          </div>

          {/* Mini trend chart placeholder - shows mock trend */}
          <div className="hidden lg:block w-64 h-20 ml-6">
            <svg viewBox="0 0 200 60" className="w-full h-full" preserveAspectRatio="none">
              <defs>
                <linearGradient id="emv-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="white" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="white" stopOpacity="0.05" />
                </linearGradient>
              </defs>
              {/* Generate simple trend line based on current EMV */}
              <path
                d={`M 0,40 L 33,35 L 66,42 L 99,30 L 132,25 L 165,20 L 200,${
                  60 - (((currentStats?.email_marketing_value || 0) / 10000) * 40)
                }`}
                fill="none"
                stroke="white"
                strokeWidth="2"
                opacity="0.8"
              />
              <path
                d={`M 0,40 L 33,35 L 66,42 L 99,30 L 132,25 L 165,20 L 200,${
                  60 - (((currentStats?.email_marketing_value || 0) / 10000) * 40)
                } L 200,60 L 0,60 Z`}
                fill="url(#emv-gradient)"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, index) => (
          <KPICard key={index} kpi={kpi} />
        ))}
      </div>

      {/* Grouped Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Email Performance */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center space-x-2 mb-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <TrendingUp className="h-4 w-4 text-blue-600" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900">Email Performance</h3>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between py-1.5">
              <span className="text-xs text-gray-600">Total Sent</span>
              <span className="text-xs font-semibold text-gray-900">{(currentStats?.total_emails_sent || 0).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between py-1.5 border-t border-gray-100">
              <span className="text-xs text-gray-600">Delivered</span>
              <span className="text-xs font-semibold text-gray-900">{(currentStats?.total_delivered || 0).toLocaleString()} ({deliveryRate.toFixed(1)}%)</span>
            </div>
            <div className="flex items-center justify-between py-1.5 border-t border-gray-100">
              <span className="text-xs text-gray-600">Unique Opens</span>
              <span className="text-xs font-semibold text-gray-900">{(currentStats?.unique_opens || 0).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between py-1.5 border-t border-gray-100">
              <span className="text-xs text-gray-600">Total Clicks</span>
              <span className="text-xs font-semibold text-gray-900">{(currentStats?.total_clicks || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Key Insights */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center space-x-2 mb-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <Eye className="h-4 w-4 text-green-600" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900">Key Insights</h3>
          </div>
          <div className="space-y-2">
            <div className="p-2 rounded-lg text-xs bg-green-50 text-green-800 font-medium">
              Strong engagement with {openRate.toFixed(1)}% open rate
            </div>
            <div className="p-2 rounded-lg text-xs bg-green-50 text-green-800 font-medium">
              Click rate above industry average
            </div>
            <div className="p-2 rounded-lg text-xs bg-green-50 text-green-800 font-medium">
              Delivery rate of {deliveryRate.toFixed(1)}%
            </div>
            <div className="p-2 rounded-lg text-xs bg-green-50 text-green-800 font-medium">
              EMV generating ${(currentStats?.email_marketing_value || 0).toLocaleString()}
            </div>
          </div>
        </div>

        {/* Subscription Metrics */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center space-x-2 mb-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Users className="h-4 w-4 text-purple-600" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900">Subscription Metrics</h3>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between py-1.5">
              <span className="text-xs text-gray-600">Total Active</span>
              <span className="text-xs font-semibold text-gray-900">{(currentStats?.active_subscriptions || 0).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between py-1.5 border-t border-gray-100">
              <span className="text-xs text-gray-600">Free Subscribers</span>
              <span className="text-xs font-semibold text-gray-900">{(currentStats?.free_subscriptions || 0).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between py-1.5 border-t border-gray-100">
              <span className="text-xs text-gray-600">Premium Subscribers</span>
              <span className="text-xs font-semibold text-gray-900">{(currentStats?.premium_subscriptions || 0).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between py-1.5 border-t border-gray-100">
              <span className="text-xs text-gray-600">Referral Program</span>
              <span className="text-xs font-semibold text-gray-900">{currentStats?.referral_program_enabled ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
        </div>
      </div>


      {/* Post Detail Drawer */}
      <PostDetailDrawer
        post={selectedPost}
        isOpen={!!selectedPost}
        onClose={() => setSelectedPost(null)}
        posts={postsStats?.posts || []}
        onNavigate={setSelectedPost}
      />
            </>
          )}

          {/* Posts Tab Content */}
          {activeTab === 'posts' && (
            <div className="p-4">
              <NewsletterPostsTable
                posts={postsStats?.posts || []}
                loading={isLoading}
              />
            </div>
          )}

          {/* Publication Stats Tab Content */}
          {activeTab === 'publication-stats' && (
            <div className="space-y-4">
              {/* Lifetime Data Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 font-medium">
                  The data shown below represents the publication's lifetime performance from account inception to date.
                </p>
              </div>

              {/* Publication Header - EMV Hero */}
              <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-500 via-emerald-500 to-teal-600 p-6 shadow-lg">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-10">
                  <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <pattern id="emv-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                        <circle cx="2" cy="2" r="1" fill="white" />
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#emv-pattern)" />
                  </svg>
                </div>

                <div className="relative flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <DollarSign className="h-5 w-5 text-white" />
                      <span className="text-sm font-medium text-white/90">Total Earned Media Value</span>
                    </div>
                    <div className="flex items-baseline space-x-3">
                      <span className="text-4xl font-bold text-white">
                        ${(currentStats?.email_marketing_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <p className="text-xs text-white/70 mt-1">Measuring the total value generated from newsletter engagement</p>
                  </div>
                  {currentStats?.referral_program_enabled && (
                    <div className="px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium text-white border border-white/30 ml-6">
                      Referral Program Active
                    </div>
                  )}
                </div>
              </div>

              {/* Button to open history details drawer */}
              {historyStats.length > 0 && (
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsHistoryDrawerOpen(true)}
                    icon={<BarChart3 className="h-4 w-4" />}
                  >
                    Lifetime Statistics
                  </Button>
                </div>
              )}

              {/* Historical Trends Charts */}
              {historyStats.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <PublicationMetricChart
                    data={historyStats}
                    title="Active Subscriptions"
                    dataKey="activeSubscriptions"
                    color="#8b5cf6"
                    formatValue={(value) => value.toLocaleString()}
                    formatTooltip={(value) => value.toLocaleString()}
                  />
                  <PublicationMetricChart
                    data={historyStats}
                    title="Email Delivery"
                    dataKey="totalSent"
                    dataKey2="totalDelivered"
                    color="#f59e0b"
                    color2="#10b981"
                  />
                  <PublicationMetricChart
                    data={historyStats}
                    title="Engagement Metrics"
                    dataKey="totalUniqueOpened"
                    dataKey2="totalClicked"
                    color="#3b82f6"
                    color2="#ec4899"
                  />
                </div>
              )}

              {historyStats.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <PublicationMetricChart
                    data={historyStats}
                    title="Engagement Rates"
                    dataKey="averageOpenRate"
                    dataKey2="averageClickRate"
                    color="#3b82f6"
                    color2="#10b981"
                    formatValue={(value) => `${value.toFixed(1)}%`}
                    formatTooltip={(value) => `${value.toFixed(2)}%`}
                    yAxisWidth={50}
                  />
                  <PublicationMetricChart
                    data={historyStats}
                    title="Earned Media Value"
                    dataKey="emv"
                    color="#10b981"
                    formatValue={(value) => `$${value >= 1000 ? (value / 1000).toFixed(1) + 'K' : value.toFixed(0)}`}
                    formatTooltip={(value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    yAxisWidth={60}
                  />
                </div>
              )}

            </div>
          )}
        </>
      )}

      {/* History Details Drawer */}
      <Drawer
        isOpen={isHistoryDrawerOpen}
        onClose={() => setIsHistoryDrawerOpen(false)}
        title="Publication Statistics (Lifetime-to-Date)"
        description={historyStats.length > 0 ? `Snapshot Date: ${new Date(historyStats[historyStats.length - 1].snapshotDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}` : ''}
        icon={<BarChart3 className="h-5 w-5" />}
        width="lg"
      >
        {historyStats.length > 0 && (() => {
          const latestStats = historyStats[historyStats.length - 1];
          return (
            <div className="space-y-3">
              {/* Earned Media Value - Prominent Display */}
              <KPICard
                kpi={{
                  label: 'Lifetime Earned Media Value',
                  value: latestStats.emv,
                  format: 'currency',
                  icon: 'dollar-sign'
                }}
              />

              {/* Subscriber Metrics */}
              <Card variant="elevated" padding="md">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 bg-purple-50 rounded-lg">
                    <Users className="h-4 w-4 text-purple-600" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900">Subscriber Metrics</h4>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <span className="text-sm font-medium text-purple-900">Active Subscriptions</span>
                    <span className="text-sm font-bold text-purple-900">{latestStats.activeSubscriptions.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Free Subscriptions</span>
                    <span className="text-sm font-bold text-gray-900">{latestStats.activeFreeSubscriptions.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Premium Subscriptions</span>
                    <span className="text-sm font-bold text-gray-900">{latestStats.activePremiumSubscriptions.toLocaleString()}</span>
                  </div>
                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-xs text-gray-600 mb-2">Subscription Breakdown</div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-blue-600"
                          style={{
                            width: `${latestStats.activeSubscriptions > 0 ? ((latestStats.activeFreeSubscriptions / latestStats.activeSubscriptions) * 100) : 0}%`
                          }}
                        />
                      </div>
                      <span className="text-xs text-gray-600 min-w-[40px]">
                        {latestStats.activeSubscriptions > 0 ? ((latestStats.activeFreeSubscriptions / latestStats.activeSubscriptions) * 100).toFixed(0) : 0}% Free
                      </span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Email Engagement */}
              <Card variant="elevated" padding="md">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 bg-blue-50 rounded-lg">
                    <TrendingUp className="h-4 w-4 text-blue-600" />
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900">Email Engagement</h4>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Total Sent</span>
                    <span className="text-sm font-bold text-gray-900">{latestStats.totalSent.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Total Delivered</span>
                    <span className="text-sm font-bold text-gray-900">{latestStats.totalDelivered.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Unique Opens</span>
                    <span className="text-sm font-bold text-gray-900">{latestStats.totalUniqueOpened.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-700">Total Clicks</span>
                    <span className="text-sm font-bold text-gray-900">{latestStats.totalClicked.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <span className="text-sm font-medium text-blue-900">Average Open Rate</span>
                    <span className="text-sm font-bold text-blue-900">{latestStats.averageOpenRate.toFixed(2)}%</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <span className="text-sm font-medium text-blue-900">Average Click Rate</span>
                    <span className="text-sm font-bold text-blue-900">{latestStats.averageClickRate.toFixed(2)}%</span>
                  </div>
                </div>
              </Card>
            </div>
          );
        })()}
      </Drawer>
    </div>
  );
};

// KPI Card Component
interface KPICardProps {
  kpi: NewsletterKPI;
}

const KPICard: React.FC<KPICardProps> = ({ kpi }) => {
  const formatValue = (value: number | string, format?: string) => {
    if (typeof value === 'string') return value;

    switch (format) {
      case 'percentage':
        return `${value.toFixed(1)}%`;
      case 'currency':
        return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      case 'number':
      default:
        return value.toLocaleString();
    }
  };

  const getIcon = (iconName?: string) => {
    switch (iconName) {
      case 'users': return <Users className="h-3.5 w-3.5 text-white" />;
      case 'eye': return <Eye className="h-3.5 w-3.5 text-white" />;
      case 'mouse-pointer': return <MousePointer className="h-3.5 w-3.5 text-white" />;
      case 'dollar-sign': return <DollarSign className="h-3.5 w-3.5 text-white" />;
      default: return <TrendingUp className="h-3.5 w-3.5 text-white" />;
    }
  };

  const getGradientColors = (iconName?: string) => {
    switch (iconName) {
      case 'dollar-sign':
        return {
          background: 'bg-gradient-to-br from-green-50 to-emerald-50',
          border: 'border-green-200',
          iconBadge: 'bg-gradient-to-br from-green-500 to-emerald-600'
        };
      case 'users':
        return {
          background: 'bg-gradient-to-br from-white to-blue-50/30',
          border: 'border-blue-100',
          iconBadge: 'bg-gradient-to-br from-blue-500 to-indigo-600'
        };
      case 'eye':
        return {
          background: 'bg-gradient-to-br from-white to-purple-50/30',
          border: 'border-purple-100',
          iconBadge: 'bg-gradient-to-br from-purple-500 to-indigo-600'
        };
      case 'mouse-pointer':
        return {
          background: 'bg-gradient-to-br from-white to-orange-50/30',
          border: 'border-orange-100',
          iconBadge: 'bg-gradient-to-br from-orange-500 to-red-600'
        };
      default:
        return {
          background: 'bg-gradient-to-br from-white to-gray-50/30',
          border: 'border-gray-100',
          iconBadge: 'bg-gradient-to-br from-gray-500 to-gray-600'
        };
    }
  };

  const colors = getGradientColors(kpi.icon);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className={`rounded-lg border p-4 ${colors.background} ${colors.border} hover:shadow-lg transition-all duration-300 group`}>
        <div className="flex items-center justify-between mb-3">
          <div className={`h-7 w-7 rounded-lg ${colors.iconBadge} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-sm`}>
            {getIcon(kpi.icon)}
          </div>
          {kpi.change && (
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
              kpi.changeType === 'increase' ? 'bg-green-100 text-green-700' :
              kpi.changeType === 'decrease' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
            }`}>
              {kpi.changeType === 'increase' ? '+' : ''}{kpi.change}%
            </span>
          )}
        </div>
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">{kpi.label}</p>
        <p className="text-2xl font-bold text-gray-900">
          {formatValue(kpi.value, kpi.format)}
        </p>
      </div>
    </motion.div>
  );
};

export default NewsletterDashboard;